import { Fragment, ReactNode, useEffect, useState } from "react";
 
import { DataTableContainer } from "./DataTables.elements";
import { OpenCloseToggleButton } from "../Buttons/OpenCloseToggleButton/OpenCloseToggleButton";
import { TableHeaders } from "./TableHeaders";
import { ConfigurableChartProps, DataItemProps } from "common/components/ConfigurableLineChart";
 
export interface DataTableProps extends ConfigurableChartProps {
  gridAreas?: string;
  griRows?: string;
  totalsKey?: string;
  virtualRows?: number;
  allowMultipleOpen?: boolean;
  filterSelected?: boolean;
  selected?: Record<string, unknown[] | undefined>;
  setSelected?: (x?: Record<string, unknown[] | undefined>) => void;
  data?: DataItemProps[];
  tableHeaders?: string[];
  title?: string;
  className?: string;
  autoSize?: boolean;
  mapKeys?: Record<string, string>;
  filterKeys?: string[];
  format?: {
    dataTable?: (x?: Record<string, unknown>) => ReactNode;
  },
  KeyValComponents?: Record<string, (x: unknown) => ReactNode>;
  ignoreKeys?: string[];
  valueKey?: string;
  totalByKeys?: string[] | string;
  itemsKey?: string;
  colors?: Record<string, string>;
  gridCols?: string;
  sortState?: Record<string, { [key: string]: 'ASC' | 'DESC' | string  | undefined }> | undefined;
  setSortState?: (x: Record<string, { [key: string]: 'ASC' | 'DESC' | string }> | undefined) => void;
  scrollVertical?: boolean | number | string;
  maxHeight?: number | string;
  handleHeaderClick?: (x?: Record<string, unknown>) => void;
  sort?: { [key: string]: 'ASC' | 'DESC' | 'asc' | 'desc' | string };
 }

 export interface TableCellProps extends  DataTableProps  {
     cellData?: unknown;
     columnData?: unknown;
     columnIndex?: number;
     dataKey?: string;
     isScrolling?: boolean;
     parent?: unknown;
     rowData?: unknown;
     rowIndex: number;
     hasItems?: boolean
 }
const DataTableCell = ({ 
  handleClick, 
  value = 'n/a',   
  id,  
  isFirst = false, 
  isLast = false,
  //total = 0, 
  dataIndex = 0,  
  col = 0, 
  row = 0, 
  data,  
  k, 
  format, 
  mapKeys,
  className, 
  itemsKey,
  ...props  
}: TableCellProps) => {
 
    let cellClass = `cell value ${className || ''}`.trim()

    if (isFirst) cellClass += ' is-first'
    if (isLast) cellClass += ' is-last'

    const handleHover = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, row: number) => {
      if (!itemsKey) return
      e?.currentTarget?.parentElement
        ?.querySelectorAll('.is-hovered')
        ?.forEach(x => x.classList.remove('is-hovered'))

      e?.currentTarget?.parentElement
        ?.querySelectorAll(`[data-rowindex='${row}']`)
        ?.forEach(x => x.classList.add('is-hovered'))

    }

    const handleHoverOut = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e?.currentTarget?.parentElement
        ?.querySelectorAll('.is-hovered')
        ?.forEach(x => x.classList.remove('is-hovered'))
    }

    const cellSet = {
      'data-index': dataIndex,
      'data-id': 'value',
      'data-cell': id,
      'data-value': value,
      'data-colindex': col,
      'data-rowindex': row,
      'data-col': col % 2 === 0 ? 'even' : 'odd',
      'data-row': row % 2 === 0 ? 'even' : 'odd',
      onMouseEnter: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => itemsKey ? handleHover(e, row) : undefined,
      onMouseLeave: handleHoverOut
    }     

    const labelSet = {
      ...cellSet
    }
    
    const formattedValue = format?.dataTable?.({value, key: k, [String(k) || '']: value, data }) || mapKeys?.[String(value)] || value
    
    if (itemsKey && (value === 'View Items' || value === 'Hide Items')) return <OpenCloseToggleButton isOpen={props?.isOpen ? true : false} data-area={k} handleClick={() => handleClick?.({[k as string]: value, data})} {...{...labelSet, className: cellClass}}>{formattedValue}</OpenCloseToggleButton>
    return <div data-area={k} onClick={() => handleClick?.({[k as string]: value, data})} {...{...labelSet, className: cellClass}}>{String(formattedValue)}</div>
    
}


 
 
const sortData = (data = [] as Record<string, unknown>[], sortState?: { [key: string]: string }) => {
 
  if (!sortState || !data?.length) return data
  const sortKey = Object.keys(sortState)[0]
  const direction = sortState[sortKey]
   
  const newData = ([...data])?.sort((a, b) => {
    if (typeof a?.[sortKey] === 'string' && typeof b?.[sortKey] === 'string') return direction === 'ASC' ? a[sortKey].localeCompare(b[sortKey]) : b[sortKey].localeCompare(a[sortKey])     
    return direction === 'ASC' ? (a[sortKey] as number) - (b[sortKey] as number) : (b[sortKey] as number) - (a[sortKey] as number)
  })

   return newData
}
 

  
 

export const DataTable = ({ 
  sort, 
  //scrollVertical = `400px`, 
  //virtualRows = 20,
  itemsKey,
  ignoreKeys, 
  gridAreas, 
  gridCols, 
  //totalsKey, 
  tableHeaders, 
  className,
  //id = new Date().getTime(),
  data,
  ...props
}: DataTableProps): JSX.Element => {
  
  const { colors, mapKeys, groupKey } = props

  //const data = useVirtualRowData({data: props?.data})
  //const lo,adItems = Array.from({ length: 21 }, (_, i) => i)
  
  const [sortState, setSortState] = useState<{ [key: string]: string } | undefined>(sort)
  const [openItems, setOpenItems] = useState<string[]>([])
   
  useEffect(() => {
    setOpenItems([])
  }, [data, sortState])
  
  if (!data) return <></>

  tableHeaders = tableHeaders || Object.keys(data[0] || {}).filter(x => !ignoreKeys?.includes(x))
   
  if (!tableHeaders?.length)  return <>no table headers</>

  className = `data-table ${className || ''}`.trim()
  
  gridAreas = gridAreas || `'${tableHeaders?.reduce((acc, key) => {
    acc += `${key as string} `
    return acc
  }, '')}'`
 
  if (!gridCols) {
    gridCols = '' as string
    `${tableHeaders?.forEach(() => {
      gridCols += 'auto '
    })}`.trim()
  }  
  
  if (itemsKey) gridCols += ' auto'
 
  data = sortData(data, sortState)

  //const dataToDisplay = data?.slice(Number(inView + 1), Number(inView + virtualRows)) || []

  const handleHeaderClick =  (key: string) => {
    if (sortState?.[key] === 'DESC') setSortState({ [key]: 'ASC' })
    else setSortState({ [key]: 'DESC' })
  }

  tableHeaders = itemsKey ? [...tableHeaders, 'viewMore'] : tableHeaders
  
 
  return(
    <DataTableContainer className={className} {...{ gridCols: gridCols, gridAreas: gridAreas}}>
      <div className='grid scroll-y scroll' data-id='data-grid' onScroll={handleScroll}>

        <TableHeaders {...{ ...props, tableHeaders, sortState, setSortState, handleHeaderClick }} />
        
        {data?.map((itemData, dataIndex) => {
          const isOpen = openItems?.includes(String(dataIndex))
          const className = isOpen ? 'is-open' : undefined
          
          const Cells = (tableHeaders as string[] || []).map((key, col, arr) => {
            const colorID = itemData?.[groupKey]
            const color = itemData?.color || colors?.[colorID] || colors?.[mapKeys?.[colorID]]
            const value = itemData[key as keyof typeof itemData]
            const cellSettings = {
              ...props,
              ...itemData,
              color,
              className,
              value: key === 'viewMore' ? !isOpen ? 'View Items' : 'Hide Items' : String(value),
              k: key,
              dataIndex,
              row: dataIndex + 1,
              col,
              isFirst: col === 0,
              isLast: col === (arr?.length || 0) - 1,
            }
            return (
              <DataTableCell key={key} {...{ ...props, ...cellSettings, itemsKey, isOpen, k: key, data: itemData, handleClick: () => itemsKey && (setOpenItems(isOpen ? undefined : [String(dataIndex)]))}} />
            )
          })
          
          return (
            <Fragment key={dataIndex}>
              {Cells}
              {isOpen && <div className='cell full items'><div className='scroll'><DataTable {...{  ignoreKeys, data: itemData?.[itemsKey as keyof typeof itemData]}} /></div></div>}
            </Fragment>
          )
        })}
      </div>
    </DataTableContainer>
  )
}

const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
  if (e) return
  const containerEl = e.currentTarget;
  const containerRect = containerEl.getBoundingClientRect()
  const headerOffset = (e.currentTarget.querySelector(`.cell.header`) as HTMLDivElement).getBoundingClientRect().height
  const scrollTop = Math.floor(containerRect.top)
  const scrollBottom = scrollTop + containerRect.height 

  const visible = []

  containerEl.querySelectorAll(`.value.is-first`).forEach((el) => {
    const elTop = Math.floor(el.getBoundingClientRect().top)

    const elPosition = Math.floor((elTop + headerOffset) - scrollTop)
    const elDataIndex = el.getAttribute('data-index')

    let isHidden = false
    if (elPosition < 0 || elTop > scrollBottom) isHidden = true
    
    
    if (!isHidden) {
      el.classList.remove('is-hidden')
      el.classList.add('is-not-hidden')
      visible.push(elDataIndex)
    }

    if (isHidden) {
      el.classList.add('is-hidden')
      el.classList.remove('is-not-hidden')
    }

    

  })
  
  containerEl.querySelectorAll('.is-hidden').forEach(el => {
    const dataIndex = el.dataset.index
    containerEl.querySelectorAll(`[data-index='${dataIndex}]`)?.forEach(rowEl => {
      rowEl.style.display = 'none'
    })
    
  })

  containerEl.querySelectorAll('.is-not-hidden').forEach(el => {
    const dataIndex = el.dataset.index
    containerEl.querySelectorAll(`[data-index='${dataIndex}]`)?.forEach(rowEl => {
      rowEl.style.display = 'flex'
    })
    
  })
    
}